import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSportContent } from '../hooks/useSportContent';
import { useUserProfile } from '../hooks/useUserProfile';
import { SportShot } from '../content';
import { aiCoachingService } from '../services/aiCoachingService';
import { Link } from 'react-router-dom';
import { isRecoveryRelevant, getRecoveryUrl } from '../config/recoveryLinks';
import { getRelatedInjuries, getRelatedShots } from '../config/injuryShotLinks';

interface SelectedAnalysis {
  shotType: string;
  issue: string;
  recommendations: string[];
  explanation?: string;
  priorityAreas?: string[];
  practiceTips?: string[];
  isAIEnhanced?: boolean;
}

const ShotDiagnosisScreen: React.FC = () => {
  const { currentSport } = useTheme();
  const { user } = useAuth();
  const { content, loading, error } = useSportContent(currentSport.id);
  const { profile, getUserSkillLevel, getWeakAreas } = useUserProfile();
  const [selectedShotType, setSelectedShotType] = useState<string>('');
  const [selectedIssue, setSelectedIssue] = useState<string>('');
  const [selectedAnalysis, setSelectedAnalysis] = useState<SelectedAnalysis | null>(null);
  const [allShots, setAllShots] = useState<SportShot[]>([]);
  const [personalizedRecommendations, setPersonalizedRecommendations] = useState<string[]>([]);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [useAI, setUseAI] = useState(true); // Toggle for AI recommendations
  const [aiAvailable, setAiAvailable] = useState(false);

  const injuryCategory = content?.shotDiagnosis?.find(a => a.id?.includes('injuries'));
  const injuryShots = injuryCategory?.shots ?? [];

  useEffect(() => {
    if (content?.shotDiagnosis) {
      // Flatten all shots from all categories
      const shots: SportShot[] = [];
      content.shotDiagnosis.forEach(analysis => {
        shots.push(...analysis.shots);
      });
      setAllShots(shots);
    }
  }, [content]);

  useEffect(() => {
    // Check if AI service is available
    setAiAvailable(aiCoachingService.isAvailable());
  }, []);

  const handleShotTypeChange = (shotType: string) => {
    setSelectedShotType(shotType);
    setSelectedIssue('');
    setSelectedAnalysis(null);
  };

  const handleIssueChange = async (issue: string) => {
    setSelectedIssue(issue);
    
    // Find the selected shot and create analysis
    const shot = allShots.find(s => s.name === selectedShotType);
    if (!shot) return;

    // Use AI if available and enabled, otherwise use static recommendations
    if (useAI && aiAvailable) {
      setIsLoadingAI(true);
      try {
        const userSkillLevel = getUserSkillLevel(currentSport.id);
        const weakAreas = getWeakAreas(currentSport.id);
        
        const aiResponse = await aiCoachingService.getCoachingRecommendations({
          sport: currentSport.name,
          shot: shot,
          issue: issue,
          skillLevel: userSkillLevel,
          weakAreas: weakAreas,
        });

        const analysis: SelectedAnalysis = {
          shotType: selectedShotType,
          issue: issue,
          recommendations: aiResponse.recommendations,
          explanation: aiResponse.explanation,
          priorityAreas: aiResponse.priorityAreas,
          practiceTips: aiResponse.practiceTips,
          isAIEnhanced: true,
        };
        setSelectedAnalysis(analysis);
        setPersonalizedRecommendations(aiResponse.recommendations);
      } catch (error) {
        console.error('Error getting AI recommendations:', error);
        // Fallback to static recommendations
        const personalizedRecs = getPersonalizedRecommendations(shot, issue);
        const analysis: SelectedAnalysis = {
          shotType: selectedShotType,
          issue: issue,
          recommendations: personalizedRecs,
          isAIEnhanced: false,
        };
        setSelectedAnalysis(analysis);
        setPersonalizedRecommendations(personalizedRecs);
      } finally {
        setIsLoadingAI(false);
      }
    } else {
      // Use static recommendations
      const personalizedRecs = getPersonalizedRecommendations(shot, issue);
      const analysis: SelectedAnalysis = {
        shotType: selectedShotType,
        issue: issue,
        recommendations: personalizedRecs,
        isAIEnhanced: false,
      };
      setSelectedAnalysis(analysis);
      setPersonalizedRecommendations(personalizedRecs);
    }
  };

  const getPersonalizedRecommendations = (shot: SportShot, issue: string): string[] => {
    const userSkillLevel = getUserSkillLevel(currentSport.id);
    const weakAreas = getWeakAreas(currentSport.id);
    
    // Start with base recommendations
    let recommendations = [...shot.improvements];
    
    // Filter and prioritize based on skill level
    if (userSkillLevel === 'beginner') {
      // Focus on fundamental improvements
      recommendations = recommendations.filter(rec => 
        rec.toLowerCase().includes('basic') || 
        rec.toLowerCase().includes('fundamental') ||
        rec.toLowerCase().includes('start') ||
        rec.toLowerCase().includes('simple')
      );
      
      // Add beginner-specific tips
      if (recommendations.length === 0) {
        recommendations = [
          "Focus on proper grip and stance first",
          "Practice the basic motion slowly",
          "Work on consistency before power",
          "Get comfortable with the fundamentals"
        ];
      }
    } else if (userSkillLevel === 'advanced') {
      // Include more technical and advanced tips
      recommendations = recommendations.filter(rec => 
        !rec.toLowerCase().includes('basic') && 
        !rec.toLowerCase().includes('start')
      );
    }
    
    // Prioritize recommendations for weak areas
    const weakAreaRecommendations = recommendations.filter(rec => {
      return weakAreas.some(weakArea => 
        rec.toLowerCase().includes(weakArea.toLowerCase()) ||
        issue.toLowerCase().includes(weakArea.toLowerCase())
      );
    });
    
    // If we have weak area recommendations, prioritize them
    if (weakAreaRecommendations.length > 0) {
      recommendations = [...weakAreaRecommendations, ...recommendations.filter(rec => !weakAreaRecommendations.includes(rec))];
    }
    
    // Limit to 4-5 most relevant recommendations
    return recommendations.slice(0, 5);
  };

  const getUniqueIssues = () => {
    const shot = allShots.find(s => s.name === selectedShotType);
    return shot ? shot.commonIssues : [];
  };

  const getSelectedShot = () => {
    return allShots.find(s => s.name === selectedShotType);
  };

  const isInjurySelected = () => injuryShots.some(s => s.name === selectedShotType);
  const relatedInjuries = selectedShotType && !isInjurySelected()
    ? getRelatedInjuries(selectedShotType, injuryShots, currentSport.id)
    : [];
  const relatedShots = selectedShotType && isInjurySelected()
    ? getRelatedShots(selectedShotType, allShots, injuryShots, currentSport.id)
    : [];

  if (loading) {
    return (
      <div className="shot-analysis-screen">
        <div className="container">
          <h1>Loading {currentSport.name} Shot Diagnosis...</h1>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shot-analysis-screen">
        <div className="container">
          <h1>Error Loading Shot Diagnosis</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shot-analysis-screen">
      <div className="container">
        <h1>{currentSport.name} Shot Diagnosis</h1>
        <p className="description">
          Get personalized recommendations for improving your shots. Select your shot type and the issue you're experiencing.
        </p>
        
        <div className="shot-analysis-interface">
          {profile && (
            <div className="personalized-insights">
              <h3>Your {currentSport.name} Profile</h3>
              <div className="skill-level">
                <strong>Skill Level:</strong> {getUserSkillLevel(currentSport.id).charAt(0).toUpperCase() + getUserSkillLevel(currentSport.id).slice(1)}
              </div>
              {getWeakAreas(currentSport.id).length > 0 && (
                <div className="weak-areas">
                  <strong>Areas to Focus On:</strong> {getWeakAreas(currentSport.id).join(', ')}
                </div>
              )}
            </div>
          )}
          
          <div className="analysis-controls">
            <div className="control-group">
              <label htmlFor="shot-type">Select Shot Type:</label>
              <select
                id="shot-type"
                value={selectedShotType}
                onChange={(e) => handleShotTypeChange(e.target.value)}
                className="shot-select"
              >
                <option value="">Choose a shot type...</option>
                {allShots.map((shot) => (
                  <option key={shot.name} value={shot.name}>
                    {shot.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedShotType && (
              <div className="control-group">
                <label htmlFor="issue">Select Issue:</label>
                <select
                  id="issue"
                  value={selectedIssue}
                  onChange={(e) => handleIssueChange(e.target.value)}
                  className="issue-select"
                >
                  <option value="">Choose an issue...</option>
                  {getUniqueIssues().map((issue, index) => (
                    <option key={index} value={issue}>
                      {issue}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {selectedShotType && (
            <div className="shot-info">
              <h3>{selectedShotType}</h3>
              <p>{getSelectedShot()?.description}</p>

              {relatedInjuries.length > 0 && (
                <div className="related-section" style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f8f0', borderRadius: '6px', border: '1px solid #4caf5040' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32', fontSize: '0.95rem' }}>🔗 Related injuries</h4>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85em', color: '#555' }}>This shot can contribute to:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {relatedInjuries.map((inj) => (
                      <button
                        key={inj.name}
                        type="button"
                        onClick={() => handleShotTypeChange(inj.name)}
                        style={{
                          padding: '0.35rem 0.75rem',
                          background: 'white',
                          color: '#2e7d32',
                          border: '1px solid #2e7d32',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                        }}
                      >
                        {inj.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {relatedShots.length > 0 && (
                <div className="related-section" style={{ marginTop: '1rem', padding: '0.75rem', background: '#e8f4f8', borderRadius: '6px', border: '1px solid #2196f340' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#1565c0', fontSize: '0.95rem' }}>🔗 Related shots</h4>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85em', color: '#555' }}>Often linked to these shots:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {relatedShots.map((shot) => (
                      <button
                        key={shot.name}
                        type="button"
                        onClick={() => handleShotTypeChange(shot.name)}
                        style={{
                          padding: '0.35rem 0.75rem',
                          background: 'white',
                          color: '#1565c0',
                          border: '1px solid #1565c0',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                        }}
                      >
                        {shot.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedShotType === 'Injuries Overview' && (
                <div className="recovery-link" style={{ marginTop: '1rem', padding: '1rem', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #4caf50' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32' }}>🏥 Experiencing pain?</h4>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9em', color: '#555' }}>
                    {user
                      ? "You're already signed in—get your personalized recovery plan on MendOnBend."
                      : 'Get a personalized recovery plan for your injury on MendOnBend.'}
                  </p>
                  <a
                    href={getRecoveryUrl(currentSport.name, 'injury prevention', 'Injuries Overview')}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '0.5rem 1rem',
                      background: '#4caf50',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.9em'
                    }}
                  >
                    Get Recovery Plan on MendOnBend →
                  </a>
                </div>
              )}
            </div>
          )}

          {aiAvailable && (
            <div className="ai-toggle" style={{ marginBottom: '1rem', padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={useAI}
                  onChange={(e) => setUseAI(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span>🤖 Use AI-Powered Recommendations</span>
              </label>
            </div>
          )}

          {isLoadingAI && (
            <div className="loading-ai" style={{ padding: '1rem', textAlign: 'center' }}>
              <p>🤖 Getting AI-powered recommendations...</p>
            </div>
          )}

          {selectedAnalysis && !isLoadingAI && (
            <div className="analysis-results">
              <h3>
                Personalized Recommendations
                {selectedAnalysis.isAIEnhanced && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.8em', color: '#666' }}>
                    🤖 AI-Enhanced
                  </span>
                )}
              </h3>
              <div className="issue-selected">
                <strong>Issue:</strong> {selectedAnalysis.issue}
              </div>
              
              {selectedAnalysis.explanation && (
                <div className="explanation" style={{ marginTop: '1rem', padding: '0.75rem', background: '#e8f4f8', borderRadius: '4px' }}>
                  <p>{selectedAnalysis.explanation}</p>
                </div>
              )}

              {selectedAnalysis.priorityAreas && selectedAnalysis.priorityAreas.length > 0 && (
                <div className="priority-areas" style={{ marginTop: '1rem' }}>
                  <strong>Priority Areas:</strong> {selectedAnalysis.priorityAreas.join(', ')}
                </div>
              )}

              <div className="recommendations" style={{ marginTop: '1rem' }}>
                <h4>Recommended Improvements:</h4>
                <ul>
                  {selectedAnalysis.recommendations.map((recommendation, index) => (
                    <li key={index}>{recommendation}</li>
                  ))}
                </ul>
              </div>

              {selectedAnalysis.practiceTips && selectedAnalysis.practiceTips.length > 0 && (
                <div className="practice-tips" style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f8f0', borderRadius: '4px' }}>
                  <h4>Practice Tips:</h4>
                  <ul>
                    {selectedAnalysis.practiceTips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {getSelectedShot()?.videoUrl && (
                <div className="video-link" style={{ marginTop: '1rem' }}>
                  <a 
                    href={getSelectedShot()?.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="video-button"
                  >
                    📹 Watch Video Tutorial
                  </a>
                </div>
              )}

              {isRecoveryRelevant(selectedAnalysis.issue, selectedAnalysis.shotType) && (
                <div className="recovery-link" style={{ marginTop: '1.5rem', padding: '1rem', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #4caf50' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32' }}>🏥 Recovery & Resilience</h4>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9em', color: '#555' }}>
                    {user
                      ? "You're already signed in—get your personalized recovery plan on MendOnBend."
                      : `Get a personalized recovery plan for your ${(selectedAnalysis.shotType || selectedAnalysis.issue).toLowerCase()} on MendOnBend.`}
                  </p>
                  <a
                    href={getRecoveryUrl(currentSport.name, selectedAnalysis.issue, selectedAnalysis.shotType)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '0.5rem 1rem',
                      background: '#4caf50',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.9em'
                    }}
                  >
                    Get Recovery Plan on MendOnBend →
                  </a>
                </div>
              )}
            </div>
          )}

          {!selectedShotType && (
            <>
              <div className="help-text">
                <h3>How to Use Shot Diagnosis</h3>
                <ol>
                  <li><strong>Select Shot Type:</strong> Choose the specific shot you want to improve</li>
                  <li><strong>Select Issue:</strong> Pick the problem you're experiencing with that shot</li>
                  <li><strong>Get Recommendations:</strong> Receive personalized tips and watch video tutorials</li>
                </ol>
              </div>
              <div className="common-injuries-section" style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0f8f0', borderRadius: '8px', border: '1px solid #4caf5040' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <h3 style={{ margin: '0 0 0.75rem 0', color: '#2e7d32' }}>🏥 Recovery and Rehab</h3>
                    <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9em', color: '#555' }}>
                      {injuryShots.length > 0
                        ? 'Select an injury for prevention tips and recovery plans. Link injuries to shot technique above.'
                        : `Get recovery plans for common ${currentSport.name} injuries.`}
                    </p>
                  </div>
                  <Link
                    to={`/recovery-rehab/${currentSport.id}`}
                    style={{
                      fontSize: '0.85rem',
                      color: '#2e7d32',
                      fontWeight: 600,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Full Recovery & Rehab →
                  </Link>
                </div>
                {injuryShots.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {injuryShots.map((shot) => (
                      <button
                        key={shot.name}
                        type="button"
                        onClick={() => handleShotTypeChange(shot.name)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: selectedShotType === shot.name ? '#2e7d32' : 'white',
                          color: selectedShotType === shot.name ? 'white' : '#2e7d32',
                          border: '1px solid #2e7d32',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }}
                      >
                        {shot.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <a
                    href={getRecoveryUrl(currentSport.name, 'injury prevention', 'Injuries Overview')}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '0.5rem 1rem',
                      background: '#4caf50',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.9em'
                    }}
                  >
                    Get Recovery Plan on MendOnBend →
                  </a>
                )}
              </div>
            </>
          )}
        </div>

        {/* Fallback: Show all shots if no interactive selection */}
        {allShots.length === 0 && (
          <div className="no-shots">
            <p>No shot diagnosis available for {currentSport.name}.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShotDiagnosisScreen;
